import {
  ExtendedCircleLayer,
  ExtendedFillLayer,
  ExtendedLineLayer,
  ExtendedRasterLayer,
  ExtendedSymbolLayer,
  LegendLayerConfig,
  MapLibreLayerSpecification
} from "../types";
import { scaleOrdinal } from "d3-scale";
import { schemeCategory10 } from "d3-scale-chromatic";
import { geostylerToMapLibreLayers } from "./geostylerToMapLibre";
import { clampMaxZoom } from "./maplibreZoom";
import { areaOutlineLayerId, areaPaint, circlePaint, linePaint } from "./layerStyle";

// Create a color scale
const colorScale = scaleOrdinal(schemeCategory10);

/**
 * Builds MapLibre layer spec(s) from a config's GeoStyler style (the new styling
 * source of truth). The converter yields renderer fragments without `id`/`source`;
 * here we attach the GeoJSON source, a stable id (extra fragments get a `::p{i}`
 * suffix), visibility, and zoom bounds. A single rule with one symbolizer yields a
 * single spec; multi-symbolizer/multi-rule styles yield several.
 */
const buildSpecsFromGeostyler = (layerConfig: LegendLayerConfig, data?: any): MapLibreLayerSpecification[] => {
  const config = layerConfig as any;
  const fragments = geostylerToMapLibreLayers(config.geostyler);
  const source = {
    type: "geojson" as const,
    data: data || config.url,
    buffer: 512,
    tolerance: 0,
    maxzoom: 24
  };
  return fragments.map((frag, i) => {
    const layout: Record<string, unknown> = {
      visibility: config.initialVisibility ? "visible" : "none",
      ...(frag.layout ?? {})
    };
    const spec: Record<string, unknown> = {
      id: i === 0 ? config.id : `${config.id}::p${i}`,
      type: frag.type,
      metadata: { ...(config.metadata ?? {}), ...frag.metadata },
      source,
      layout,
      minzoom: frag.minzoom ?? config.options?.minZoom ?? 0,
      maxzoom: clampMaxZoom(frag.maxzoom ?? config.options?.maxZoom)
    };
    if (frag.paint) spec.paint = frag.paint;
    if (frag.filter) spec.filter = frag.filter;
    return spec as unknown as MapLibreLayerSpecification;
  });
};

/** A layer limited to one geometry family (external data layers are) carries that filter. */
const geometryFilterOf = (layerConfig: LegendLayerConfig): { filter?: any } => {
  const filter = (layerConfig as { geometryFilter?: unknown[] }).geometryFilter;
  return filter ? { filter } : {};
};

/**
 * Helper function to determine if a value is a zoom-dependent expression
 * Checks if the value is an array where the first element is 'step' or 'interpolate'
 * and the second element is ['zoom']
 */
const isZoomDependentExpression = (value: any): boolean => {
  return Array.isArray(value) && (value[0] === "step" || value[0] === "interpolate") && Array.isArray(value[1]) && value[1][0] === "zoom";
};

/**
 * Transforms layer configuration and fetched data to MapLibre layer specification.
 * Take the layer definition, and also the fetched data for that layer with the proper Auth headers
 * TODO: Could we add the data to the LegendLayerConfig object?
 */
export const transformToMapLibreLayer = (
  layerConfig: LegendLayerConfig,
  data?: any // Optionally overwrite the url with fetched data
): MapLibreLayerSpecification | MapLibreLayerSpecification[] | undefined => {
  if (layerConfig.type === "geojson") {
    // New styling path: when the layer carries a GeoStyler style, render it
    // exclusively via our converter (no legacy shorthand fallback for this layer).
    if ((layerConfig as any).geostyler) {
      return buildSpecsFromGeostyler(layerConfig, data);
    }
    if (layerConfig.geometry === "line") {
      return {
        id: layerConfig.id,
        type: "line",
        metadata: layerConfig?.metadata || {},
        source: {
          type: "geojson",
          data: data || layerConfig.url,
          buffer: 512,
          tolerance: 0,
          maxzoom: 24 // Added maxzoom: 24
        },
        paint: linePaint(layerConfig.style),
        ...geometryFilterOf(layerConfig),
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
      } as ExtendedLineLayer;
    } else if (layerConfig.geometry === "symbol") {
      // This condition checks if the layer has a default icon specified.
      // We only apply icon/text visibility settings for symbol layers that are intended to display icons.
      // Symbol layers without a defaultIcon might be text-only, so we skip applying these settings.
      if (layerConfig.defaultIcon) {
        const layout: any = {
          visibility: layerConfig.initialVisibility ? "visible" : "none",
          "icon-image": layerConfig.style.iconStyle || layerConfig.defaultIcon,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "symbol-placement": "point",
          "symbol-z-order": "source",
          "icon-optional": false, // Never hide icons, even if text is placed
          "text-optional": true, // Text can be hidden if needed
          "symbol-sort-key": 1 // Higher priority for symbols
        };

        // Handle zoom-dependent icon size
        if (isZoomDependentExpression(layerConfig.style.iconSize)) {
          layout["icon-size"] = layerConfig.style.iconSize;
        } else {
          layout["icon-size"] = layerConfig.style.iconSize;
        }

        const paint: any = {};

        /** Implement Labels */
        if (layerConfig.style.labelField) {
          layout["text-field"] = layerConfig.style.labelField;

          // Handle zoom-dependent text size
          if (isZoomDependentExpression(layerConfig.style.labelSize)) {
            layout["text-size"] = layerConfig.style.labelSize;
          } else {
            layout["text-size"] = layerConfig.style.labelSize || 12;
          }

          // Handle zoom-dependent font
          if (isZoomDependentExpression(layerConfig.style.labelFont)) {
            layout["text-font"] = layerConfig.style.labelFont;
          } else {
            layout["text-font"] = layerConfig.style.labelFont || ["Mukta, sans-serif"];
          }

          // Handle zoom-dependent offset
          if (isZoomDependentExpression(layerConfig.style.labelOffset)) {
            layout["text-offset"] = layerConfig.style.labelOffset;
          } else {
            layout["text-offset"] = layerConfig.style.labelOffset || [1, 0];
          }

          // Handle zoom-dependent text color
          if (isZoomDependentExpression(layerConfig.style.labelColor)) {
            paint["text-color"] = layerConfig.style.labelColor;
          } else {
            paint["text-color"] = layerConfig.style.labelColor || "#ffffff";
          }

          // Handle optional text-opacity
          if (layerConfig.style.labelOpacity !== undefined) {
            if (isZoomDependentExpression(layerConfig.style.labelOpacity)) {
              paint["text-opacity"] = layerConfig.style.labelOpacity;
            } else {
              paint["text-opacity"] = layerConfig.style.labelOpacity;
            }
          }

          // Handle optional text-halo (outline)
          if (layerConfig.style.labelHaloColor !== undefined) {
            paint["text-halo-color"] = layerConfig.style.labelHaloColor;
            paint["text-halo-width"] = layerConfig.style.labelHaloWidth || 1;
          }
        }

        return {
          id: layerConfig.id,
          type: "symbol", // Change "point", "line", or "fill" to "symbol"
          metadata: layerConfig?.metadata || {},
          source: {
            type: "geojson",
            data: data || layerConfig.url,
            buffer: 512,
            tolerance: 0,
            maxzoom: 24
          },
          layout: layout,
          paint: paint,
          ...geometryFilterOf(layerConfig),
          minzoom: layerConfig.options?.minZoom ?? 0,
          maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
        } as ExtendedSymbolLayer;
      }
    } else if (layerConfig.geometry === "circle") {
      // Plain point markers, e.g. an external data layer with no icon (see `defaultLayerPaint`).
      return {
        id: layerConfig.id,
        type: "circle",
        metadata: layerConfig?.metadata || {},
        source: {
          type: "geojson",
          data: data || layerConfig.url,
          buffer: 512,
          tolerance: 0,
          maxzoom: 24
        },
        layout: {
          visibility: layerConfig.initialVisibility ? "visible" : "none"
        },
        paint: circlePaint(layerConfig.style),
        ...geometryFilterOf(layerConfig),
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
      } as ExtendedCircleLayer;
    } else if (layerConfig.geometry === "fill" && layerConfig.styleByField) {
      let paint: any = {};
      let layout: any = {
        visibility: layerConfig.initialVisibility ? "visible" : "none"
      };

      // Collect unique names from the GeoJSON data
      const uniqueNames: Set<string> = new Set();
      let dataIsValid = false;

      if (data && data.features && Array.isArray(data.features) && data.features.length > 0) {
        dataIsValid = true;

        data.features.forEach((feature: any) => {
          if (feature.properties && feature.properties[layerConfig.styleByField!] !== undefined) {
            uniqueNames.add(feature.properties[layerConfig.styleByField!]);
          }
        });
      } else {
        // TODO: Sometimes we get to this point and the data property is still just referencing a url
      }

      const uniqueNamesArray = Array.from(uniqueNames); // Convert set to array if needed

      // Handle case where we have valid data with categories to style by
      if (dataIsValid && uniqueNamesArray.length > 0) {
        const colorExpressions = uniqueNamesArray.flatMap((name) => [
          ["==", ["get", layerConfig.styleByField!], name], // Condition
          colorScale(name) // Color value corresponding to the condition
        ]);

        paint = {
          "fill-color": [
            "case",
            ...colorExpressions,
            "#000000" // Default color
          ],
          "fill-opacity": 0.8,
          "fill-outline-color": "#000000"
        };

        // Apply custom styles if provided
        if ("opacity" in layerConfig.style) {
          paint["fill-opacity"] = layerConfig.style.opacity;
        }

        // Handle outline color if provided
        if ("fill-outline-color" in layerConfig.style) {
          paint["fill-outline-color"] = layerConfig.style["fill-outline-color" as keyof typeof layerConfig.style];
        }
      } else {
        // Fallback to a simple style when data isn't available yet
        paint = {
          "fill-color": "#4F1106", // Use a default color
          "fill-opacity": 0.8,
          "fill-outline-color": "#000000"
        };

        // Apply custom styles if provided
        if ("opacity" in layerConfig.style) {
          paint["fill-opacity"] = layerConfig.style.opacity;
        }

        // Handle outline color if provided
        if ("fill-outline-color" in layerConfig.style) {
          paint["fill-outline-color"] = layerConfig.style["fill-outline-color" as keyof typeof layerConfig.style];
        }
      }

      return {
        id: layerConfig.id,
        type: "fill",
        metadata: layerConfig?.metadata || {},
        source: {
          type: "geojson",
          data: data || layerConfig.url,
          buffer: 512,
          tolerance: 0,
          maxzoom: 24
        },
        layout: layout,
        paint: paint,
        ...geometryFilterOf(layerConfig),
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
      } as ExtendedFillLayer; // Use a type assertion here
    } else if (layerConfig.geometry === "fill") {
      // Simple fill layer without styleByField. An outline wider than MapLibre's
      // 1 px fill outline is a line layer drawn over the fill.
      const { fill, outline } = areaPaint(layerConfig.style);
      const source = {
        type: "geojson" as const,
        data: data || layerConfig.url,
        buffer: 512,
        tolerance: 0,
        maxzoom: 24
      };
      const layout = { visibility: layerConfig.initialVisibility ? ("visible" as const) : ("none" as const) };
      const minzoom = layerConfig.options?.minZoom ?? 0;
      const maxzoom = clampMaxZoom(layerConfig.options?.maxZoom);
      const fillLayer = {
        id: layerConfig.id,
        type: "fill",
        metadata: layerConfig?.metadata || {},
        source,
        layout,
        paint: fill,
        ...geometryFilterOf(layerConfig),
        minzoom,
        maxzoom
      } as ExtendedFillLayer;
      if (!outline) return fillLayer;
      const outlineLayer = {
        id: areaOutlineLayerId(layerConfig.id),
        type: "line",
        metadata: layerConfig?.metadata || {},
        source,
        layout,
        paint: outline,
        ...geometryFilterOf(layerConfig),
        minzoom,
        maxzoom
      } as ExtendedLineLayer;
      return [fillLayer, outlineLayer];
    }
    // Implement other cases (raster, raster-dem, etc.) based on your application's requirements

    return undefined; // Return null for unsupported or failed transformations
  } else if (layerConfig.type === "raster") {
    // Add WMS layers etc. (WMS layers are called type: raster in MapLibre GL JS)
    return {
      id: layerConfig.id,
      type: "raster",
      // @ts-ignore
      source: {
        type: "raster",
        tiles: [layerConfig.url],
        tileSize: layerConfig.options.tileSize || 256
      },
      paint: {
        "raster-opacity": layerConfig.options.opacity || 1 // Use the opacity value from the layer's options object, or default to 1 if it's not set
      },
      layout: {
        visibility: layerConfig.initialVisibility ? "visible" : "none"
      }
    } as ExtendedRasterLayer;
  } else if (layerConfig.type === "pmtiles") {
    // Both raster and vector PMTiles render via native MapLibre (deck.gl removed — §7.3).
    if (layerConfig.layerFormat === "raster") {
      return {
        id: layerConfig.id,
        type: "raster",
        metadata: (layerConfig as any)?.metadata || {},
        source: {
          type: "raster",
          url: `pmtiles://${layerConfig.url}`,
          tileSize: layerConfig.options?.tileSize || 256
        },
        paint: {
          "raster-opacity": layerConfig.options?.opacity ?? 1,
          "raster-fade-duration": 300
        },
        layout: {
          visibility: layerConfig.initialVisibility ? "visible" : "none"
        },
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
      } as ExtendedRasterLayer;
    }

    // Vector PMTiles
    const layerType = layerConfig.style?.type || "fill";
    const paint: any = layerConfig.style?.paint || layerConfig.style || {};
    const layout: any = {
      ...(layerConfig.style?.layout || {}),
      visibility: layerConfig.initialVisibility ? "visible" : "none"
    };

    const result: any = {
      id: layerConfig.id,
      type: layerType,
      metadata: (layerConfig as any)?.metadata || {},
      source: {
        id: `${layerConfig.id}-source`,
        type: "vector",
        url: `pmtiles://${layerConfig.url}`
      },
      paint,
      layout,
      minzoom: layerConfig.options?.minZoom ?? 0,
      maxzoom: clampMaxZoom(layerConfig.options?.maxZoom)
    };

    if (layerConfig.sourceLayer) {
      result["source-layer"] = layerConfig.sourceLayer;
    }

    return result;
  } else if (layerConfig.type === "cog") {
    // maxZoom: 0 is a sentinel for "use native zoom from TileJSON" — use || 24
    // (not ?? 24) so 0 falls back correctly. The actual tile ceiling comes from
    // the TileJSON returned by the cog:// protocol handler.
    const cogMaxZoom = clampMaxZoom(layerConfig.options?.maxZoom || 24);
    const cogMinZoom = layerConfig.options?.minZoom || 0;
    return {
      id: layerConfig.id,
      type: "raster",
      metadata: (layerConfig as any)?.metadata || {},
      source: {
        type: "raster",
        url: `cog://${layerConfig.url}`,
        tileSize: layerConfig.options?.tileSize || 256
      },
      paint: {
        "raster-opacity": layerConfig.options?.opacity || 1,
        "raster-fade-duration": 300,
        "raster-resampling": "linear"
      },
      layout: {
        visibility: layerConfig.initialVisibility ? "visible" : "none"
      },
      minzoom: cogMinZoom,
      maxzoom: cogMaxZoom
    } as ExtendedRasterLayer;
  }
};
